import React, { useState, useEffect } from 'react';
import { TrendingUp, TrendingDown, Minus } from "lucide-react";
import { CartesianGrid, Line, LineChart, XAxis, YAxis } from "recharts";

import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

// Sample forecast data based on the API output structure
const forecastData = {
  quarterly: {
    forecast_period: "quarterly",
    forecast_months: 3,
    waste_types: [
      {
        waste_type: "organic",
        trend_analysis: {
          trend_direction: 0,
          trend_label: "Stable"
        },
        forecast: {
          monthly_breakdown: [
            { month: 1, projected_weight: 63192.31 },
            { month: 2, projected_weight: 84047.1 },
            { month: 3, projected_weight: 97548.63 }
          ]
        }
      },
      {
        waste_type: "paper",
        trend_analysis: {
          trend_direction: 0,
          trend_label: "Stable"
        },
        forecast: {
          monthly_breakdown: [
            { month: 1, projected_weight: 47486.83 },
            { month: 2, projected_weight: 59171.73 },
            { month: 3, projected_weight: 60564.06 }
          ]
        }
      },
      {
        waste_type: "pmd",
        trend_analysis: {
          trend_direction: 0,
          trend_label: "Stable"
        },
        forecast: {
          monthly_breakdown: [
            { month: 1, projected_weight: 67310.08 },
            { month: 2, projected_weight: 80459.01 },
            { month: 3, projected_weight: 80911.97 }
          ]
        }
      },
      {
        waste_type: "residual",
        trend_analysis: {
          trend_direction: 0,
          trend_label: "Stable"
        },
        forecast: {
          monthly_breakdown: [
            { month: 1, projected_weight: 34495.76 },
            { month: 2, projected_weight: 41296.17 },
            { month: 3, projected_weight: 48096.58 }
          ]
        }
      }
    ]
  },
  "6months": {
    forecast_period: "6months",
    forecast_months: 6,
    waste_types: [
      {
        waste_type: "organic",
        trend_analysis: {
          trend_direction: 1,
          trend_label: "Uptrend"
        },
        forecast: {
          monthly_breakdown: [
            { month: 1, projected_weight: 63192.31 },
            { month: 2, projected_weight: 84047.1 },
            { month: 3, projected_weight: 97548.63 },
            { month: 4, projected_weight: 105000 },
            { month: 5, projected_weight: 112000 },
            { month: 6, projected_weight: 118000 }
          ]
        }
      },
      {
        waste_type: "paper",
        trend_analysis: {
          trend_direction: -1,
          trend_label: "Downtrend"
        },
        forecast: {
          monthly_breakdown: [
            { month: 1, projected_weight: 47486.83 },
            { month: 2, projected_weight: 59171.73 },
            { month: 3, projected_weight: 60564.06 },
            { month: 4, projected_weight: 58000 },
            { month: 5, projected_weight: 55000 },
            { month: 6, projected_weight: 52000 }
          ]
        }
      },
      {
        waste_type: "pmd",
        trend_analysis: {
          trend_direction: 1,
          trend_label: "Uptrend"
        },
        forecast: {
          monthly_breakdown: [
            { month: 1, projected_weight: 67310.08 },
            { month: 2, projected_weight: 80459.01 },
            { month: 3, projected_weight: 80911.97 },
            { month: 4, projected_weight: 85000 },
            { month: 5, projected_weight: 89000 },
            { month: 6, projected_weight: 93000 }
          ]
        }
      },
      {
        waste_type: "residual",
        trend_analysis: {
          trend_direction: 0,
          trend_label: "Stable"
        },
        forecast: {
          monthly_breakdown: [
            { month: 1, projected_weight: 34495.76 },
            { month: 2, projected_weight: 41296.17 },
            { month: 3, projected_weight: 48096.58 },
            { month: 4, projected_weight: 49000 },
            { month: 5, projected_weight: 50000 },
            { month: 6, projected_weight: 51000 }
          ]
        }
      }
    ]
  },
  yearly: {
    forecast_period: "yearly",
    forecast_months: 12,
    waste_types: [
      {
        waste_type: "organic",
        trend_analysis: {
          trend_direction: 0,
          trend_label: "Stable"
        },
        forecast: {
          monthly_breakdown: [
            { month: 1, projected_weight: 63192.31 },
            { month: 2, projected_weight: 84047.1 },
            { month: 3, projected_weight: 97548.63 },
            { month: 4, projected_weight: 97815.87 },
            { month: 5, projected_weight: 97815.87 },
            { month: 6, projected_weight: 97815.87 },
            { month: 7, projected_weight: 97815.87 },
            { month: 8, projected_weight: 97815.87 },
            { month: 9, projected_weight: 97815.87 },
            { month: 10, projected_weight: 97815.87 },
            { month: 11, projected_weight: 97815.87 },
            { month: 12, projected_weight: 97815.87 }
          ]
        }
      },
      {
        waste_type: "paper",
        trend_analysis: {
          trend_direction: 0,
          trend_label: "Stable"
        },
        forecast: {
          monthly_breakdown: [
            { month: 1, projected_weight: 47486.83 },
            { month: 2, projected_weight: 59171.73 },
            { month: 3, projected_weight: 60564.06 },
            { month: 4, projected_weight: 60564.06 },
            { month: 5, projected_weight: 60564.06 },
            { month: 6, projected_weight: 60564.06 },
            { month: 7, projected_weight: 60564.06 },
            { month: 8, projected_weight: 60564.06 },
            { month: 9, projected_weight: 60564.06 },
            { month: 10, projected_weight: 60564.06 },
            { month: 11, projected_weight: 60564.06 },
            { month: 12, projected_weight: 60564.06 }
          ]
        }
      },
      {
        waste_type: "pmd",
        trend_analysis: {
          trend_direction: 0,
          trend_label: "Stable"
        },
        forecast: {
          monthly_breakdown: [
            { month: 1, projected_weight: 67310.08 },
            { month: 2, projected_weight: 80459.01 },
            { month: 3, projected_weight: 80911.97 },
            { month: 4, projected_weight: 80911.97 },
            { month: 5, projected_weight: 80911.97 },
            { month: 6, projected_weight: 80911.97 },
            { month: 7, projected_weight: 80911.97 },
            { month: 8, projected_weight: 80911.97 },
            { month: 9, projected_weight: 80911.97 },
            { month: 10, projected_weight: 80911.97 },
            { month: 11, projected_weight: 80911.97 },
            { month: 12, projected_weight: 80911.97 }
          ]
        }
      },
      {
        waste_type: "residual",
        trend_analysis: {
          trend_direction: 0,
          trend_label: "Stable"
        },
        forecast: {
          monthly_breakdown: [
            { month: 1, projected_weight: 34495.76 },
            { month: 2, projected_weight: 41296.17 },
            { month: 3, projected_weight: 48096.58 },
            { month: 4, projected_weight: 54896.99 },
            { month: 5, projected_weight: 61697.4 },
            { month: 6, projected_weight: 68181.99 },
            { month: 7, projected_weight: 69825.23 },
            { month: 8, projected_weight: 69825.23 },
            { month: 9, projected_weight: 69825.23 },
            { month: 10, projected_weight: 69825.23 },
            { month: 11, projected_weight: 69825.23 },
            { month: 12, projected_weight: 69825.23 }
          ]
        }
      }
    ]
  }
};

const chartConfig = {
  organic: {
    label: "Organic",
    color: "#28a745",
  },
  paper: {
    label: "Paper",
    color: "#007bff",
  },
  pmd: {
    label: "PMD",
    color: "#ffc107",
  },
  residual: {
    label: "Residual",
    color: "#fd7e14",
  },
} satisfies ChartConfig;

const FutureWasteForecast: React.FC = () => {
  const [selectedPeriod, setSelectedPeriod] = useState<'quarterly' | '6months' | 'yearly'>('quarterly');
  const [chartData, setChartData] = useState<any[]>([]);
  const [trendInfo, setTrendInfo] = useState<any>(null);

  const periodLabels = {
    quarterly: "3 Months",
    "6months": "6 Months",
    yearly: "12 Months"
  };

  const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
                     "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

  useEffect(() => {
    const currentData = forecastData[selectedPeriod];

    // Transform data for the chart
    const transformedData = [];
    const maxMonths = currentData.forecast_months;

    for (let month = 1; month <= maxMonths; month++) {
      const dataPoint: any = {
        month: monthNames[month - 1],
        monthNumber: month
      };

      currentData.waste_types.forEach(wasteType => {
        const monthData = wasteType.forecast.monthly_breakdown.find(m => m.month === month);
        if (monthData) {
          // Convert grams to kg for better readability
          dataPoint[wasteType.waste_type] = Math.round(monthData.projected_weight / 1000 * 100) / 100;
        }
      });

      transformedData.push(dataPoint);
    }

    setChartData(transformedData);

    // Calculate overall trend
    const trends = currentData.waste_types.map(wt => wt.trend_analysis.trend_direction);
    const avgTrend = trends.reduce((sum, trend) => sum + trend, 0) / trends.length;
    const dominantTrend = currentData.waste_types.reduce((prev, current) =>
      Math.abs(current.trend_analysis.trend_direction) > Math.abs(prev.trend_analysis.trend_direction) ? current : prev
    );

    setTrendInfo({
      direction: avgTrend > 0.1 ? 'up' : avgTrend < -0.1 ? 'down' : 'stable',
      label: dominantTrend.trend_analysis.trend_label,
      period: periodLabels[selectedPeriod]
    });
  }, [selectedPeriod]);

  const getTrendIcon = () => {
    if (!trendInfo) return <Minus className="h-4 w-4" />;
    switch (trendInfo.direction) {
      case 'up': return <TrendingUp className="h-4 w-4" />;
      case 'down': return <TrendingDown className="h-4 w-4" />;
      default: return <Minus className="h-4 w-4" />;
    }
  };

  const getTrendText = () => {
    if (!trendInfo) return "Analyzing trends...";
    const direction = trendInfo.direction === 'up' ? 'increasing' :
                     trendInfo.direction === 'down' ? 'decreasing' : 'stable';
    return `Waste generation ${direction} over ${trendInfo.period.toLowerCase()}`;
  };

  return (
    <Card className="w-full">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <div className="space-y-1">
          <CardTitle className="text-base sm:text-lg">Waste Generation Forecast</CardTitle>
          <CardDescription>
            Projected waste by category - {periodLabels[selectedPeriod]}
          </CardDescription>
        </div>
        <Select value={selectedPeriod} onValueChange={(value: any) => setSelectedPeriod(value)}>
          <SelectTrigger className="w-32">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="quarterly">3 Months</SelectItem>
            <SelectItem value="6months">6 Months</SelectItem>
            <SelectItem value="yearly">12 Months</SelectItem>
          </SelectContent>
        </Select>
      </CardHeader>
      <CardContent>
        <ChartContainer config={chartConfig}>
          <LineChart
            accessibilityLayer
            data={chartData}
            margin={{
              left: 12,
              right: 12,
              top: 12,
              bottom: 12,
            }}
          >
            <CartesianGrid vertical={false} />
            <XAxis
              dataKey="month"
              tickLine={false}
              axisLine={false}
              tickMargin={8}
            />
            <YAxis
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              tickFormatter={(value) => `${value}kg`}
            />
            <ChartTooltip
              cursor={false}
              content={<ChartTooltipContent
                formatter={(value, name) => [
                  `${Number(value).toLocaleString()} kg `,
                  chartConfig[name as keyof typeof chartConfig]?.label || name
                ]}
                labelFormatter={(label) => `Month: ${label}`}
              />}
            />
            <Line
              dataKey="organic"
              type="monotone"
              stroke="var(--color-organic)"
              strokeWidth={2}
              dot={false}
            />
            <Line
              dataKey="paper"
              type="monotone"
              stroke="var(--color-paper)"
              strokeWidth={2}
              dot={false}
            />
            <Line
              dataKey="pmd"
              type="monotone"
              stroke="var(--color-pmd)"
              strokeWidth={2}
              dot={false}
            />
            <Line
              dataKey="residual"
              type="monotone"
              stroke="var(--color-residual)"
              strokeWidth={2}
              dot={false}
            />
          </LineChart>
        </ChartContainer>
      </CardContent>
      <CardFooter>
        <div className="flex w-full items-start gap-2 text-sm">
          <div className="grid gap-2">
            <div className="flex items-center gap-2 leading-none font-medium">
              {getTrendText()} {getTrendIcon()}
            </div>
            <div className="text-muted-foreground flex items-center gap-2 leading-none">
              Forecast based on historical waste collection data
            </div>
          </div>
        </div>
      </CardFooter>
    </Card>
  );
};

export default FutureWasteForecast;

